import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/formatCurrency';
import i18next from 'i18next';
import settingsService from './settingsService';

export const printReceipt = async (device, companyData) => {
  // Ottieni le impostazioni di stampa
  const { data: settings } = await settingsService.get();
  
  // Determina la lingua da usare per la stampa
  const printLanguage = settings.receiptLanguage === 'system' ? 
    i18next.language : 
    settings.receiptLanguage;
  
  // Funzione di traduzione che usa la lingua specifica per la stampa
  const t = (key, options) => i18next.t(key, { ...options, lng: printLanguage });
  
  try {
    // Selezione del tipo di stampante
    if (settings.printerType === 'a4') {
      return printReceiptA4(device, companyData, settings, t);
    } else {
      return printReceiptThermal(device, companyData, settings, t);
    }
  } catch (error) {
    console.error('Errore durante la stampa:', error);
    throw error;
  }
};

// Aggiungo una funzione helper per formattare la data con o senza orario
const formatDateWithOptionalTime = (date, settings, t) => {
  if (!date) return '';
  
  // Controlla se l'ora è diversa da 00:00
  const parsedDate = new Date(date);
  const hasSpecificTime = parsedDate && (parsedDate.getHours() !== 0 || parsedDate.getMinutes() !== 0);
  
  if (hasSpecificTime) {
    // Se c'è un orario specifico, formatta con data e ora (senza la scritta receipt.time:)
    return `${formatDate(date)} ${parsedDate.getHours().toString().padStart(2, '0')}:${parsedDate.getMinutes().toString().padStart(2, '0')}`;
  }
  
  // Altrimenti, formatta solo la data
  return formatDate(date);
};

// Funzione per la stampa con stampante termica (codice originale)
const printReceiptThermal = async (device, companyData, settings, t) => {
  try {
    // Crea un iframe nascosto per la stampa
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    document.body.appendChild(printFrame);

    // Stili per la ricevuta con larghezza dinamica
    const styles = `
      <style>
        @page {
          size: ${companyData.receiptWidth || '58mm'} auto;
          margin: 0;
        }
        @media print {
          body {
            width: ${companyData.receiptWidth || '58mm'};
            margin: 0;
            padding: 5mm;
          }
          .receipt {
            width: 100%;
            max-width: ${companyData.receiptWidth || '58mm'};
          }
        }
        body {
          font-family: Arial, sans-serif;
          font-size: 10pt;
          line-height: 1.2;
        }
        .receipt {
          text-align: center;
        }
        .receipt-header {
          margin-bottom: 3mm;
        }
        .company-logo {
          max-width: 90%;
          height: auto;
          max-height: 20mm;
          margin: 0 auto 3mm;
          display: block;
          object-fit: contain;
          border-radius: 2mm;
          padding: 1mm;
          background-color: white;
        }
        .receipt-divider {
          border-top: 1px dashed #000;
          margin: 2mm 0;
        }
        .receipt-content {
          text-align: left;
        }
        .receipt-row {
          display: flex;
          justify-content: space-between;
          margin: 1mm 0;
        }
        .price-info {
          margin: 2mm 0;
        }
        .receipt-footer {
          margin-top: 3mm;
          font-size: 9pt;
          text-align: center;
        }
        h3, h4 {
          margin: 2mm 0;
          font-size: 11pt;
        }
        p {
          margin: 1mm 0;
        }
      </style>
    `;

    // Formatta i problemi con traduzione, includendo il supporto per problemi personalizzati
    const problems = Array.isArray(device.problems) 
      ? device.problems
          .filter(p => p)
          .map(p => {
            if (p.startsWith('Altro:')) {
              const customProblem = p.substring(6); // Rimuove "Altro:" dal problema
              return `${t('models.device.problems.Altro')}: ${customProblem}`;
            }
            return t(`models.device.problems.${p}`);
          })
          .join(', ') 
      : device.problems 
        ? (device.problems.startsWith('Altro:') 
           ? `${t('models.device.problems.Altro')}: ${device.problems.substring(6)}`
           : t(`models.device.problems.${device.problems}`)) 
        : '';

    // Contenuto HTML della ricevuta
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t('receipt.title')} #${device.repairNumber}</title>
          ${styles}
        </head>
        <body>
          <div class="receipt">
            <div class="receipt-header">
              ${companyData.logo ? `<img src="${companyData.logo}" class="company-logo" alt="${companyData.companyName || 'Logo'}" />` : ''}
              <h3>${companyData.companyName || t('companyName')}</h3>
              <p>${companyData.address || t('address')}</p>
              ${companyData.region ? `<p>${companyData.region}</p>` : ''}
              <p>Tel: ${companyData.phone || t('phone')}</p>
            </div>

            <div class="receipt-divider"></div>

            <h4>${t('receipt.title')} ${device.repairNumber}</h4>
            <p>${t('receipt.date')}: ${formatDate(device.entryDate)}</p>

            <div class="receipt-divider"></div>

            <div class="device-info">
              <p>${t('receipt.device.info')}: ${device.brand} ${device.model}</p>
              <p>${t('receipt.device.problem')}: ${problems}</p>
              ${device.serialNumber ? `<p>${t('receipt.device.serialNumber')}: ${device.serialNumber}</p>` : ''}
              ${device.customerPhone ? `<p>${t('receipt.device.customerPhone')}: ${device.customerPhone}</p>` : ''}
            </div>

            <div class="receipt-divider"></div>

            <div class="price-info">
              <p>${t('receipt.payment.repairPrice')}: ${formatCurrency(parseFloat(device.price || 0), settings.currency)}</p>
              ${device.deposit > 0 ? 
                `<p>${t('receipt.payment.deposit')}: ${formatCurrency(parseFloat(device.deposit), settings.currency)}</p>` 
                : ''
              }
              ${settings.showVAT ? `
                <div class="receipt-divider"></div>
                <p>${t('receipt.payment.netPrice')}: ${formatCurrency(parseFloat(device.price || 0), settings.currency)}</p>
                <p>${t('receipt.payment.vat')}: ${settings.vatPercentage}%: ${formatCurrency(parseFloat(device.price || 0) * settings.vatPercentage / 100, settings.currency)}</p>
                <p><strong>${t('receipt.payment.totalWithVAT')}: ${formatCurrency(parseFloat(device.price || 0) + (parseFloat(device.price || 0) * settings.vatPercentage / 100), settings.currency)}</strong></p>
              ` : `
              <p><strong>${
                device.paymentStatus === 'pagato' ? 
                t('receipt.payment.paid') : 
                `${t('receipt.payment.toPay')}: ${formatCurrency(
                  parseFloat(device.price || 0) - parseFloat(device.deposit || 0), 
                  settings.currency
                )}`
              }</strong></p>
              `}
            </div>

            <div class="receipt-divider"></div>

            ${device.deliveryDate ? `
              <p>${t('receipt.expectedDelivery')}: ${formatDateWithOptionalTime(device.deliveryDate, settings, t)}</p>
              <div class="receipt-divider"></div>
            ` : ''}

            <div class="receipt-footer">
              <p>${companyData.disclaimer || t('receipt.footer.thanks')}</p>
              <p>${t('receipt.footer.warranty.prefix')} ${companyData.warrantyDays || 30} ${t('receipt.footer.warranty.suffix')}</p>
              <div class="receipt-divider"></div>
              <p>gext.it</p>
            </div>
          </div>
        </body>
      </html>
    `;

    // Scrivi il contenuto nell'iframe
    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(content);
    frameDoc.close();

    // Aspetta che il contenuto sia caricato
    return new Promise((resolve, reject) => {
      printFrame.onload = () => {
        try {
          // Imposta le dimensioni dell'iframe in base alla larghezza della ricevuta
          const width = companyData.receiptWidth || '58mm';
          printFrame.style.width = width;
          
          // Stampa dopo un breve ritardo per assicurarsi che gli stili siano applicati
          setTimeout(() => {
            printFrame.contentWindow.print();
            
            // Rimuovi l'iframe dopo la stampa
            setTimeout(() => {
              document.body.removeChild(printFrame);
              resolve();
            }, 1000);
          }, 100);
        } catch (error) {
          reject(error);
        }
      };
    });
  } catch (error) {
    console.error('Errore durante la stampa termica:', error);
    throw error;
  }
};

// Nuova funzione per la stampa con stampante A4
const printReceiptA4 = async (device, companyData, settings, t) => {
  try {
    // Crea un iframe nascosto per la stampa
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    document.body.appendChild(printFrame);

    // Formatta i problemi con traduzione, includendo il supporto per problemi personalizzati
    const problems = Array.isArray(device.problems) 
      ? device.problems
          .filter(p => p)
          .map(p => {
            if (p.startsWith('Altro:')) {
              const customProblem = p.substring(6); // Rimuove "Altro:" dal problema
              return `${t('models.device.problems.Altro')}: ${customProblem}`;
            }
            return t(`models.device.problems.${p}`);
          })
          .join(', ') 
      : device.problems 
        ? (device.problems.startsWith('Altro:') 
           ? `${t('models.device.problems.Altro')}: ${device.problems.substring(6)}`
           : t(`models.device.problems.${device.problems}`)) 
        : '';

    // Stili per la ricevuta A4 (formato un quarto di pagina)
    const styles = `
      <style>
        @page {
          size: A4;
          margin: 10mm;
        }
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
        }
        body {
          font-family: Arial, sans-serif;
          font-size: 9pt;
          line-height: 1.1;
        }
        .receipt-container {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-gap: 8mm;
          padding: 3mm;
          max-width: 100%;
          max-height: 50vh; /* Limita all'altezza a metà pagina */
        }
        .receipt {
          border: 1px solid #ccc;
          border-radius: 4mm;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          box-sizing: border-box;
          padding: 6mm;
          overflow: hidden;
          page-break-inside: avoid;
          height: auto;
          width: 100%; /* Allargata la ricevuta */
          max-height: calc(50vh - 20mm); /* Limita l'altezza per stare in metà pagina */
          margin: 0 auto;
        }
        .receipt-header {
          text-align: center;
          margin-bottom: 2mm;
          padding-bottom: 2mm;
          border-bottom: 1px solid #ddd;
        }
        .company-logo {
          max-width: 60%; /* Aumentato per allargare il logo */
          height: auto;
          max-height: 15mm;
          margin: 0 auto 2mm;
          display: block;
          object-fit: contain;
        }
        .receipt-divider {
          border-top: 1px solid #ddd;
          margin: 2mm 0;
        }
        .receipt-section {
          margin: 2mm 0;
        }
        .receipt-footer {
          margin-top: 2mm;
          text-align: center;
          font-size: 7pt;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 2mm;
        }
        .footer-disclaimer {
          font-size: 6pt; /* Font più piccolo per i termini e condizioni */
          margin: 1mm 0;
        }
        .footer-warranty {
          font-size: 6.5pt; /* Font leggermente più grande della disclaimer */
          margin: 1mm 0;
        }
        .footer-brand {
          margin-top: 1mm;
          font-size: 8pt;
          text-align: center;
          font-weight: bold;
          color: #333;
        }
        h3 {
          margin: 1mm 0;
          font-size: 11pt;
          color: #333;
        }
        h4 {
          margin: 1mm 0;
          font-size: 10pt;
          color: #333;
        }
        .receipt-info {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1mm;
        }
        .receipt-col {
          flex: 1;
          min-width: 46%;
        }
        .field-label {
          font-weight: bold;
          color: #555;
        }
        p {
          margin: 0.5mm 0;
          font-size: 8pt;
        }
        .price-row {
          display: flex;
          justify-content: space-between;
          margin: 0.5mm 0;
          font-size: 8pt;
        }
        .price-total {
          font-weight: bold;
          font-size: 9pt;
        }
      </style>
    `;

    // Generazione di una singola ricevuta
    const createReceiptHtml = () => `
      <div class="receipt">
        <div class="receipt-header">
          ${companyData.logo ? `<img src="${companyData.logo}" class="company-logo" alt="${companyData.companyName || 'Logo'}" />` : ''}
          <h3>${companyData.companyName || t('companyName')}</h3>
          <p>${companyData.address || t('address')}</p>
          ${companyData.region ? `<p>${companyData.region}</p>` : ''}
          <p>Tel: ${companyData.phone || t('phone')}</p>
        </div>

        <div class="receipt-section" style="display: flex; justify-content: space-between; align-items: center;">
          <h4>${t('receipt.title')} ${device.repairNumber}</h4>
          <p>${t('receipt.date')}: ${formatDate(device.entryDate)}</p>
        </div>

        <div class="receipt-divider"></div>

        <div class="receipt-section receipt-info">
          <div class="receipt-col">
            <p><span class="field-label">${t('receipt.device.info')}:</span> ${device.brand} ${device.model}</p>
            <p><span class="field-label">${t('receipt.device.problem')}:</span> ${problems}</p>
          </div>
          <div class="receipt-col">
            ${device.serialNumber ? `<p><span class="field-label">${t('receipt.device.serialNumber')}:</span> ${device.serialNumber}</p>` : ''}
            ${device.customerPhone ? `<p><span class="field-label">${t('receipt.device.customerPhone')}:</span> ${device.customerPhone}</p>` : ''}
            ${device.deliveryDate ? `<p><span class="field-label">${t('receipt.expectedDelivery')}:</span> ${formatDateWithOptionalTime(device.deliveryDate, settings, t)}</p>` : ''}
          </div>
        </div>

        <div class="receipt-divider"></div>

        <div class="receipt-section">
          <div class="price-row">
            <span class="field-label">${t('receipt.payment.repairPrice')}:</span>
            <span>${formatCurrency(parseFloat(device.price || 0), settings.currency)}</span>
          </div>
          ${device.deposit > 0 ? `
            <div class="price-row">
              <span class="field-label">${t('receipt.payment.deposit')}:</span>
              <span>${formatCurrency(parseFloat(device.deposit), settings.currency)}</span>
            </div>
          ` : ''}
          
          ${settings.showVAT ? `
            <div class="receipt-divider"></div>
            <div class="price-row">
              <span class="field-label">${t('receipt.payment.netPrice')}:</span>
              <span>${formatCurrency(parseFloat(device.price || 0), settings.currency)}</span>
            </div>
            <div class="price-row">
              <span class="field-label">${t('receipt.payment.vat')}: ${settings.vatPercentage}%:</span>
              <span>${formatCurrency(parseFloat(device.price || 0) * settings.vatPercentage / 100, settings.currency)}</span>
            </div>
            <div class="price-row price-total">
              <span class="field-label">${t('receipt.payment.totalWithVAT')}:</span>
              <span>${formatCurrency(parseFloat(device.price || 0) + (parseFloat(device.price || 0) * settings.vatPercentage / 100), settings.currency)}</span>
            </div>
          ` : `
            <div class="price-row price-total">
              <span class="field-label">${device.paymentStatus === 'pagato' ? t('receipt.payment.paid') : t('receipt.payment.toPay')}:</span>
              <span>${
                device.paymentStatus === 'pagato' ? 
                formatCurrency(parseFloat(device.price || 0), settings.currency) : 
                formatCurrency(parseFloat(device.price || 0) - parseFloat(device.deposit || 0), settings.currency)
              }</span>
            </div>
          `}
        </div>

        <div class="receipt-footer">
          <p class="footer-disclaimer">${companyData.disclaimer || t('receipt.footer.thanks')}</p>
          <p class="footer-warranty">${t('receipt.footer.warranty.prefix')} ${companyData.warrantyDays || 30} ${t('receipt.footer.warranty.suffix')}</p>
          <div class="footer-brand">gext.it</div>
        </div>
      </div>
    `;

    // Determina il numero di ricevute da stampare
    // In modalità duplicata: stampa 2 copie, in modalità singola: stampa solo 1 ricevuta
    const numReceipts = settings.printDuplicate ? 2 : 1;
    const receiptsHtml = Array(numReceipts).fill('').map(createReceiptHtml).join('');

    // Contenuto HTML completo per la pagina A4
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t('receipt.title')} #${device.repairNumber}</title>
          ${styles}
        </head>
        <body>
          <div class="receipt-container">
            ${receiptsHtml}
          </div>
        </body>
      </html>
    `;

    // Scrivi il contenuto nell'iframe
    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(content);
    frameDoc.close();

    // Aspetta che il contenuto sia caricato
    return new Promise((resolve, reject) => {
      printFrame.onload = () => {
        try {
          // Stampa dopo un breve ritardo per assicurarsi che gli stili siano applicati
          setTimeout(() => {
            printFrame.contentWindow.print();
            
            // Rimuovi l'iframe dopo la stampa
            setTimeout(() => {
              document.body.removeChild(printFrame);
              resolve();
            }, 1000);
          }, 100);
        } catch (error) {
          reject(error);
        }
      };
    });
  } catch (error) {
    console.error('Errore durante la stampa A4:', error);
    throw error;
  }
}; 